import React from 'react';
import type { Player } from '@/types';
import { getTeamDisplayName } from '@/utils/team';

interface PlayerCardProps {
  player: Player;
  className?: string;
  onClick?: (player: Player) => void;
}

const PlayerCard: React.FC<PlayerCardProps> = ({
  player,
  className = '',
  onClick,
}) => {
  // 计算ELO和排名变化的显示
  const getChangeDisplay = (change: number) => {
    if (change > 0) {
      return {
        color: 'text-positive',
        icon: '↗',
        text: `+${change}`,
      };
    } else if (change < 0) {
      return {
        color: 'text-negative',
        icon: '↘',
        text: `${change}`,
      };
    } else {
      return {
        color: 'text-neutral',
        icon: '—',
        text: '0',
      };
    }
  };

  const eloChange = getChangeDisplay(player.eloChange6Month);
  const rankChange = getChangeDisplay(-player.rankChange6Month); // 排名变化是相反的

  const handleClick = () => {
    if (onClick) {
      onClick(player);
    }
  };

  return (
    <div
      className={`
        bg-white 
        border border-border 
        rounded-xl 
        p-6 
        shadow-card 
        hover:shadow-card-hover 
        hover:border-glory-gold 
        transition-all 
        duration-300 
        cursor-pointer
        ${className}
      `}
      onClick={handleClick}
      role='button'
      tabIndex={0}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      {/* 选手ID和真实姓名 */}
      <div className='mb-4'>
        <h3 className='text-text-primary font-medium text-base'>
          {player.playerId}
          {player.realName && (
            <span className='text-text-secondary ml-2'>
              · {player.realName}
            </span>
          )}
        </h3>
      </div>

      {/* ELO分数显示 */}
      <div className='mb-4 text-center'>
        <div
          className='text-glory-gold font-bold leading-tight'
          style={{
            fontSize: 'var(--font-size-elo)',
            fontFamily: 'var(--font-family-data)',
          }}
        >
          {player.currentElo.toLocaleString()} ELO
        </div>
      </div>

      {/* 排名和战队信息 */}
      <div className='mb-4 space-y-2'>
        <div className='flex justify-between items-center text-sm'>
          <span className='text-text-secondary'>排名:</span>
          <span
            className='text-glory-gold-dark font-semibold'
            style={{ fontSize: 'var(--font-size-rank)' }}
          >
            #{player.currentRank}
          </span>
        </div>

        {player.currentTeam && (
          <div className='flex justify-between items-center text-sm'>
            <span className='text-text-secondary'>战队:</span>
            <span className='text-text-primary font-medium'>
              {getTeamDisplayName(player.currentTeam)}
            </span>
          </div>
        )}
      </div>

      {/* 6月变化信息 */}
      <div className='pt-3 border-t border-border'>
        <div className='text-xs text-text-secondary mb-1'>6个月变化:</div>
        <div className='flex justify-between items-center text-sm'>
          <div className={`flex items-center gap-1 ${eloChange.color}`}>
            <span className='text-base'>{eloChange.icon}</span>
            <span className='font-medium'>{eloChange.text} ELO</span>
          </div>

          {player.rankChange6Month !== 0 && (
            <div className={`flex items-center gap-1 ${rankChange.color}`}>
              <span className='text-base'>{rankChange.icon}</span>
              <span className='font-medium'>
                {Math.abs(player.rankChange6Month)} 排名
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlayerCard;
